/**
 * @file ModulesPage.tsx
 * @brief Super Admin → Modules — platform-wide product availability.
 *
 * @details
 * Dedicated control plane for Interpretation, Notes, Meeting Intel, and
 * Medical Assessment. Writes `{FEATURE}_PLATFORM_ENABLED` via
 * PUT /api/admin/settings. Locked modules stay hidden in apps and cannot be
 * enabled from company Features. Layout is a 2×2 grid with row-tall icons
 * (09-09-2026).
 *
 * @author DES
 * @date 09-09-2026
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useEffect, useMemo, useState } from "react";
import {
  Languages,
  LoaderCircle,
  Save,
  PenLine,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  useSystemSettings,
  useUpdateSettings,
} from "@/features/billing/api";
import { companyFeaturesKeys } from "@/features/company-features/api";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useQueryClient } from "@tanstack/react-query";
import {
  PLATFORM_MODULES,
  parseModuleEnabled,
  type PlatformModuleKey,
} from "./moduleCatalog";

const MODULE_ICONS = {
  interpretation: Languages,
  notes: PenLine, // DES 08-10-2026: app icon, was StickyNote
  boardroom: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
  medical_assessment: MedicalReport, // DES 08-10-2026: app icon, was FileHeart
} as const;

type DraftMap = Record<PlatformModuleKey, boolean>;

export function ModulesPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useSystemSettings();
  const update = useUpdateSettings();
  const settings = data?.settings;

  const stored = useMemo(() => {
    const next = {} as DraftMap;
    for (const mod of PLATFORM_MODULES) {
      next[mod.key] = parseModuleEnabled(settings?.[mod.settingKey]?.value);
    }
    return next;
  }, [settings]);

  const [draft, setDraft] = useState<DraftMap | null>(null);
  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const dirtyKeys = useMemo(() => {
    if (!draft) return [] as PlatformModuleKey[];
    return PLATFORM_MODULES.map((m) => m.key).filter(
      (key) => draft[key] !== stored[key],
    );
  }, [draft, stored]);

  const onSave = async () => {
    if (!draft || dirtyKeys.length === 0) return;
    const patch: Record<string, string> = {};
    for (const mod of PLATFORM_MODULES) {
      if (!dirtyKeys.includes(mod.key)) continue;
      patch[mod.settingKey] = draft[mod.key] ? "1" : "0";
    }
    try {
      await update.mutateAsync({ settings: patch });
      void qc.invalidateQueries({ queryKey: companyFeaturesKeys.all });
      toast({
        variant: "success",
        title: "Modules updated",
        description: `${dirtyKeys.length} module${dirtyKeys.length === 1 ? "" : "s"} saved.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not update modules",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Modules</h2>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Turn products on or off for the whole platform. Locked modules
                disappear from every company app and cannot be enabled from
                company Features until you unlock them here.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              disabled={dirtyKeys.length === 0 || update.isPending}
              onClick={() => void onSave()}
            >
              {update.isPending ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save changes
            </Button>
          </div>
        </GridBand>

        {isLoading || !draft ? (
          <GridCell>
            <SkeletonFields fields={4} />
          </GridCell>
        ) : isError ? (
          <GridCell>
            <p className="text-sm text-destructive">
              Couldn&apos;t load module settings. Refresh and try again.
            </p>
          </GridCell>
        ) : (
          <GridCells className="grid-cols-1 sm:grid-cols-2">
            {PLATFORM_MODULES.map((mod) => {
              const Icon = MODULE_ICONS[mod.key];
              const enabled = draft[mod.key];
              const dirty = dirtyKeys.includes(mod.key);
              return (
                <GridCell
                  key={mod.key}
                  padded={false}
                  className="min-h-[10.5rem] border-border"
                >
                  <div className="flex h-full min-h-[8.5rem] items-stretch">
                    <span className="flex w-[8.5rem] shrink-0 items-center justify-center border-r border-border bg-muted text-muted-foreground">
                      <Icon
                        className="h-12 w-12 sm:h-14 sm:w-14"
                        strokeWidth={1.4}
                      />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col justify-between gap-3 p-4 md:p-5">
                      <div>
                        <div className="flex items-center gap-2 font-medium">
                          {mod.label}
                          {dirty ? (
                            <span className="text-xs font-normal text-muted-foreground">
                              Unsaved
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm leading-snug text-muted-foreground">
                          {enabled
                            ? mod.description
                            : "Locked off platform-wide — hidden in apps and company Features."}
                        </p>
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={update.isPending}
                        onCheckedChange={(next) =>
                          setDraft((prev) =>
                            prev ? { ...prev, [mod.key]: next } : prev,
                          )
                        }
                        aria-label={`${mod.label} platform availability`}
                      />
                    </div>
                  </div>
                </GridCell>
              );
            })}
          </GridCells>
        )}
      </GridBlock>
    </div>
  );
}
